import { startTransition, useDeferredValue, useEffect, useMemo, useState } from "react"
import Icon from "../../Icon"
import api from "../../../utils/api"
import { formatMoney } from "../../../utils/format"

const CATEGORY_ORDER = ["recurring", "non-recurring", "other"]

const toTitleCase = (value = "") =>
  String(value || "")
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")

const formatCategoryLabel = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized === "recurring") return "Recurring Cycles Group"
  if (normalized === "non-recurring") return "Event Group (Non-recurring)"
  return "Other"
}

const formatCount = (value) => (Number.isFinite(Number(value)) ? String(Number(value)) : "--")

const formatGroupTypeLabel = (group = {}) => {
  const useCase = String(group?.useCase || "").trim().toLowerCase()
  if (useCase === "chamaa-merry-go-round") return "Chamaa"
  if (useCase === "group-project-savings") return "Project Savings"
  if (useCase === "welfare-support-group") return "Welfare"

  const normalizedType = String(group?.type || "").trim()
  if (normalizedType) return toTitleCase(normalizedType)

  if (useCase === "generic-recurring") return "Recurring"
  if (useCase === "generic-non-recurring") return "Non-recurring"
  return "Other"
}

const formatRoleLabel = (member = {}) => {
  const role = String(member?.role || "member").toLowerCase()
  const adminTitle = String(member?.adminTitle || "none").toLowerCase()
  if (role !== "admin") return "Member"
  if (adminTitle && adminTitle !== "none" && adminTitle !== "admin") {
    return `Admin (${toTitleCase(adminTitle)})`
  }
  return "Admin"
}

const getMembershipBadgeClass = (value = "") => {
  const normalized = String(value || "").toLowerCase()
  if (normalized === "in-cycle") return "bg-emerald-50 text-emerald-700 border-emerald-200"
  if (normalized === "on-hold") return "bg-amber-50 text-amber-700 border-amber-200"
  return "bg-gray-50 text-gray-600 border-gray-200"
}

const getGroupStatusBadgeClass = (value = "") => {
  const normalized = String(value || "").toLowerCase()
  if (normalized === "active") return "bg-emerald-50 text-emerald-700 border-emerald-200"
  if (normalized === "planning") return "bg-sky-50 text-sky-700 border-sky-200"
  if (normalized === "closed") return "bg-gray-100 text-gray-700 border-gray-200"
  return "bg-gray-50 text-gray-600 border-gray-200"
}

const matchesGroupSearch = (group, query) => {
  const search = String(query || "").trim().toLowerCase()
  if (!search) return true
  const members = Array.isArray(group?.members) ? group.members : []
  const searchableParts = [
    group?.name,
    group?.type,
    group?.useCase,
    group?.category,
    group?.adminName,
    formatGroupTypeLabel(group),
    formatCategoryLabel(group?.category),
    ...members.flatMap((member) => [
      member?.name,
      member?.phone,
      member?.role,
      member?.adminTitle,
      member?.membershipStatus,
    ]),
  ]

  return searchableParts.some((value) => String(value || "").toLowerCase().includes(search))
}

const matchesDefaulterSearch = (row, query) => {
  const search = String(query || "").trim().toLowerCase()
  if (!search) return true
  return [
    row?.groupName,
    row?.groupCategory,
    row?.groupType,
    row?.groupUseCase,
    row?.memberName,
    row?.memberPhone,
    row?.memberRole,
    row?.memberAdminTitle,
    row?.membershipStatus,
  ].some((value) => String(value || "").toLowerCase().includes(search))
}

function AdminGroupsModuleSection({ onImpersonateUser }) {
  const [collapsed, setCollapsed] = useState(true)
  const [recurringCollapsed, setRecurringCollapsed] = useState(true)
  const [nonRecurringCollapsed, setNonRecurringCollapsed] = useState(true)
  const [otherCollapsed, setOtherCollapsed] = useState(true)
  const [defaultersCollapsed, setDefaultersCollapsed] = useState(true)

  const [typeCollapsedByKey, setTypeCollapsedByKey] = useState({})
  const [typeShowCountByKey, setTypeShowCountByKey] = useState({})
  const [groupCollapsedById, setGroupCollapsedById] = useState({})
  const [groupMemberShowCountById, setGroupMemberShowCountById] = useState({})
  const [defaultersShowCount, setDefaultersShowCount] = useState(5)

  const [searchInput, setSearchInput] = useState("")
  const deferredSearch = useDeferredValue(searchInput)

  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryLoaded, setSummaryLoaded] = useState(false)
  const [summaryError, setSummaryError] = useState("")

  const [groups, setGroups] = useState([])
  const [defaulters, setDefaulters] = useState([])
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsLoaded, setDetailsLoaded] = useState(false)
  const [detailsError, setDetailsError] = useState("")
  const [groupPendingDelete, setGroupPendingDelete] = useState(null)
  const [deleteGroupLoading, setDeleteGroupLoading] = useState(false)
  const [deleteGroupError, setDeleteGroupError] = useState("")

  const shouldLoadDetails =
    !collapsed &&
    (!defaultersCollapsed || !recurringCollapsed || !nonRecurringCollapsed || !otherCollapsed)

  const loadSummary = async () => {
    try {
      setSummaryLoading(true)
      setSummaryError("")
      const res = await api.get("/groups/admin/summary")
      setSummary(res?.data?.summary || null)
      setSummaryLoaded(true)
    } catch (error) {
      console.error("Failed to load admin groups summary", error)
      setSummaryError(error?.response?.data?.error || "Failed to load group summary.")
      setSummaryLoaded(true)
    } finally {
      setSummaryLoading(false)
    }
  }

  const loadDetails = async () => {
    try {
      setDetailsLoading(true)
      setDetailsError("")
      const res = await api.get("/groups/admin/all")
      setGroups(Array.isArray(res?.data?.groups) ? res.data.groups : [])
      setDefaulters(Array.isArray(res?.data?.defaulters) ? res.data.defaulters : [])
      setDetailsLoaded(true)
    } catch (error) {
      console.error("Failed to load admin groups details", error)
      setDetailsError(error?.response?.data?.error || "Failed to load groups.")
      setDetailsLoaded(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  useEffect(() => {
    if (!summaryLoaded && !summaryLoading) loadSummary()
  }, [summaryLoaded, summaryLoading])

  useEffect(() => {
    if (shouldLoadDetails && !detailsLoaded && !detailsLoading) {
      loadDetails()
    }
  }, [shouldLoadDetails, detailsLoaded, detailsLoading])

  useEffect(() => {
    setDefaultersShowCount(5)
  }, [deferredSearch])

  const filteredGroups = useMemo(() => {
    if (!detailsLoaded) return []
    return groups.filter((group) => matchesGroupSearch(group, deferredSearch))
  }, [groups, detailsLoaded, deferredSearch])

  const filteredDefaulters = useMemo(() => {
    if (!detailsLoaded) return []
    return [...defaulters]
      .filter((row) => matchesDefaulterSearch(row, deferredSearch))
      .sort((a, b) => Number(b?.dueAmount || 0) - Number(a?.dueAmount || 0))
  }, [defaulters, detailsLoaded, deferredSearch])

  const groupedByCategory = useMemo(() => {
    const grouped = { recurring: {}, "non-recurring": {}, other: {} }
    for (const group of filteredGroups) {
      const categoryKey = CATEGORY_ORDER.includes(group?.category) ? group.category : "other"
      const typeKey = String(group?.useCase || group?.type || "other")
      if (!grouped[categoryKey][typeKey]) {
        grouped[categoryKey][typeKey] = { label: formatGroupTypeLabel(group), groups: [] }
      }
      grouped[categoryKey][typeKey].groups.push(group)
    }
    return grouped
  }, [filteredGroups])

  const detailsCounts = useMemo(() => {
    const recurringCount = Object.values(groupedByCategory.recurring).reduce((sum, entry) => sum + entry.groups.length, 0)
    const nonRecurringCount = Object.values(groupedByCategory["non-recurring"]).reduce((sum, entry) => sum + entry.groups.length, 0)
    const otherCount = Object.values(groupedByCategory.other).reduce((sum, entry) => sum + entry.groups.length, 0)
    const totalMembers = groups.reduce(
      (sum, group) => sum + Number(group?.membersCount || (Array.isArray(group?.members) ? group.members.length : 0)),
      0
    )
    return {
      totalGroups: groups.length,
      totalMembers,
      recurringCount,
      nonRecurringCount,
      otherCount,
    }
  }, [groupedByCategory, groups])

  const summaryCategoryCounts = summary?.categoryCounts || {}
  const groupsCountText = detailsLoaded ? formatCount(detailsCounts.totalGroups) : formatCount(summary?.totalGroups)
  const membersCountText = detailsLoaded ? formatCount(detailsCounts.totalMembers) : formatCount(summary?.totalMembers)
  const recurringCountText = detailsLoaded ? formatCount(detailsCounts.recurringCount) : formatCount(summaryCategoryCounts?.recurring)
  const nonRecurringCountText = detailsLoaded ? formatCount(detailsCounts.nonRecurringCount) : formatCount(summaryCategoryCounts?.["non-recurring"])
  const defaultersCountText = detailsLoaded
    ? formatCount(filteredDefaulters.length)
    : deferredSearch
      ? "--"
      : formatCount(summary?.defaultersCount)

  const handleRefresh = async () => {
    await loadSummary()
    if (detailsLoaded || shouldLoadDetails) await loadDetails()
  }

  const openDeleteGroupModal = (group) => {
    if (!group?.id) return
    setDeleteGroupError("")
    setGroupPendingDelete(group)
  }

  const closeDeleteGroupModal = () => {
    if (deleteGroupLoading) return
    setDeleteGroupError("")
    setGroupPendingDelete(null)
  }

  const handleDeleteGroup = async () => {
    const targetGroupId = String(groupPendingDelete?.id || "")
    if (!targetGroupId || deleteGroupLoading) return

    try {
      setDeleteGroupLoading(true)
      setDeleteGroupError("")
      await api.delete(`/groups/admin/${targetGroupId}`)
      setGroupPendingDelete(null)
      await loadSummary()
      if (detailsLoaded || shouldLoadDetails) {
        await loadDetails()
      }
    } catch (error) {
      setDeleteGroupError(error?.response?.data?.error || "Failed to delete group.")
    } finally {
      setDeleteGroupLoading(false)
    }
  }

  const renderGroupCard = (group) => {
    const members = Array.isArray(group?.members) ? group.members : []
    const membersCount = Number(group?.membersCount || members.length)
    const groupDefaulters = members.filter((member) => !!member?.hasOutstandingDebt)
    const isGroupCollapsed = groupCollapsedById[group.id] ?? true
    const memberShowCount = Number(groupMemberShowCountById[group.id] || 5)
    const visibleMembers = members.slice(0, memberShowCount)

    return (
      <div key={group.id} className="bg-white relative">
        <button
          onClick={() =>
            setGroupCollapsedById((prev) => ({
              ...prev,
              [group.id]: !isGroupCollapsed,
            }))
          }
          className="w-full flex items-center justify-between p-3 pr-12 sm:pr-14 text-left hover:bg-gray-50 transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h6 className="text-sm font-semibold text-gray-900 truncate">{group.name}</h6>
              {groupDefaulters.length > 0 && (
                <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-50 text-[10px] font-semibold text-red-600 border border-red-100 px-1">
                  {groupDefaulters.length}!
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500">
              <span className={`uppercase tracking-wider font-medium ${group.status === 'active' ? 'text-emerald-600' : 'text-gray-400'}`}>
                {group.status || "unknown"}
              </span>
              <span className="text-gray-300">•</span>
              <span>{formatGroupTypeLabel(group)}</span>
              <span className="text-gray-300">•</span>
              <span>Admin: {group.adminName || "Unknown"}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 ml-3">
            <div className="hidden sm:flex items-center gap-4 text-xs">
              <div className="text-right">
                <p className="text-gray-400 text-[9px] uppercase font-semibold">Members</p>
                <p className="font-semibold text-gray-900">{membersCount}</p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-[9px] uppercase font-semibold">Wallet</p>
                <p className="font-semibold text-gray-900">{formatMoney(group.groupWalletBalance || 0)}</p>
              </div>
            </div>
            <div className="sm:hidden text-right">
              <p className="font-semibold text-gray-900 text-xs">{membersCount} <span className="text-[10px] text-gray-400 font-normal">Memb.</span></p>
              <p className="font-semibold text-gray-700 text-xs">{formatMoney(group.groupWalletBalance || 0)}</p>
            </div>
            <Icon name={isGroupCollapsed ? "chevron-right" : "chevron-down"} size="0.8rem" className="text-gray-400" />
          </div>
        </button>
        <button
          onClick={() => openDeleteGroupModal(group)}
          className="absolute right-2 top-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full inline-flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors"
          aria-label="Delete group"
          title="Delete group"
        >
          <Icon name="trash" size="0.75rem" />
        </button>

        {!isGroupCollapsed && (
          <div className="bg-gray-50/30 border-t border-gray-100">
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full text-left text-[11px]">
                <thead className="bg-gray-100/50 text-gray-500 font-semibold uppercase tracking-wider border-b border-gray-100">
                  <tr>
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Role / Status</th>
                    <th className="px-4 py-2 text-right">Paid</th>
                    <th className="px-4 py-2 text-right">Due</th>
                    <th className="px-4 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleMembers.map((member) => (
                    <tr key={`${group.id}-${member.id}`} className="hover:bg-white transition-colors">
                      <td className="px-4 py-2">
                        <p className="font-semibold text-gray-900">{member.name || "Unknown"}</p>
                        <p className="text-[10px] text-gray-400">{member.phone || "No phone"}</p>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600 font-medium">{formatRoleLabel(member)}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase border ${getMembershipBadgeClass(member.membershipStatus)}`}>
                            {String(member.membershipStatus || "none").replace(/-/g, " ")}
                          </span>
                          {member.hasOutstandingDebt && <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-red-50 text-red-600 border border-red-100">Defaulter</span>}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-700">{formatMoney(member.paid || 0)}</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-semibold ${Number(member.dueAmount || 0) > 0 ? "text-red-600" : "text-gray-400"}`}>
                          {formatMoney(member.dueAmount || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => onImpersonateUser?.(member.id, { destination: "/app/groups" })}
                          className="px-2.5 py-1 rounded bg-emerald-600 text-white text-[10px] font-semibold uppercase hover:bg-emerald-700 transition-colors"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile List */}
            <div className="sm:hidden divide-y divide-gray-100">
              {visibleMembers.map((member) => (
                <div key={`${group.id}-${member.id}`} className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 text-xs truncate">{member.name || "Unknown"}</p>
                        {member.hasOutstandingDebt && <span className="text-[8px] font-semibold uppercase bg-red-50 text-red-600 px-1 rounded border border-red-100">D</span>}
                      </div>
                      <p className="text-[10px] text-gray-400 font-medium">{member.phone || "No phone"}</p>
                    </div>
                    <button
                      onClick={() => onImpersonateUser?.(member.id, { destination: "/app/groups" })}
                      className="px-2.5 py-1 rounded bg-emerald-600 text-white text-[9px] font-semibold uppercase hover:bg-emerald-700 transition-colors"
                    >
                      View
                    </button>
                  </div>
                  
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex flex-wrap gap-1.5">
                      <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-tight bg-gray-100 px-1.5 py-0.5 rounded">{formatRoleLabel(member)}</span>
                      <span className={`text-[9px] font-semibold uppercase tracking-tight px-1.5 py-0.5 rounded border ${getMembershipBadgeClass(member.membershipStatus)}`}>
                        {String(member.membershipStatus || "none").replace(/-/g, " ")}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-[8px] text-gray-400 font-semibold uppercase">Paid</p>
                        <p className="text-[10px] font-semibold text-gray-700">{formatMoney(member.paid || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-gray-400 font-semibold uppercase">Due</p>
                        <p className={`text-[10px] font-semibold ${Number(member.dueAmount || 0) > 0 ? "text-red-600" : "text-gray-300"}`}>
                          {formatMoney(member.dueAmount || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {(members.length > 5 || memberShowCount > 5) && (
              <div className="p-2 flex justify-center gap-2 border-t border-gray-100">
                {members.length > memberShowCount && (
                  <button
                    onClick={() => setGroupMemberShowCountById(prev => ({ ...prev, [group.id]: memberShowCount + 5 }))}
                    className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide hover:underline"
                  >
                    + {members.length - memberShowCount} More
                  </button>
                )}
                {memberShowCount > 5 && (
                  <button
                    onClick={() => setGroupMemberShowCountById(prev => ({ ...prev, [group.id]: 5 }))}
                    className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide hover:underline"
                  >
                    Less
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderCategorySection = (categoryKey, isCollapsed, setIsCollapsed) => {
    const entries = Object.entries(groupedByCategory[categoryKey]).sort(([, left], [, right]) => left.label.localeCompare(right.label))
    const categoryCount = entries.reduce((sum, [, entry]) => sum + entry.groups.length, 0)

    return (
      <div className="bg-white overflow-hidden">
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="w-full flex items-center justify-between px-6 py-2.5 text-left hover:bg-gray-50 transition-colors group/cat"
        >
          <div className="flex items-center gap-2.5">
            <Icon name={categoryKey === "recurring" ? "arrow-repeat" : "calendar-event"} size="0.8rem" className="text-gray-400 group-hover/cat:text-emerald-600 transition-colors" />
            <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight group-hover/cat:text-gray-900">{formatCategoryLabel(categoryKey)}</span>
            <span className="text-[10px] font-bold text-gray-400">
              {detailsLoaded ? categoryCount : formatCount(summaryCategoryCounts?.[categoryKey])}
            </span>
          </div>
          <Icon name={isCollapsed ? "chevron-right" : "chevron-down"} size="0.8rem" className="text-gray-400 transition-transform duration-200" />
        </button>

        {!isCollapsed && (
          <div className="divide-y divide-gray-100 bg-gray-50/20">
            {!detailsLoaded ? (
              <div className="p-4 text-center text-[11px] text-gray-400 uppercase font-bold tracking-widest">
                {detailsLoading ? "Loading..." : "Expand to Load"}
              </div>
            ) : entries.length === 0 ? (
              <div className="p-4 text-center text-[11px] text-gray-400 uppercase font-bold tracking-widest">Empty</div>
            ) : (
              entries.map(([typeKey, entry]) => {
                const sectionKey = `${categoryKey}:${typeKey}`
                const typeCollapsed = typeCollapsedByKey[sectionKey] ?? true
                const typeShowCount = Number(typeShowCountByKey[sectionKey] || 5)
                const visibleGroups = entry.groups.slice(0, typeShowCount)

                return (
                  <div key={sectionKey}>
                    <button
                      onClick={() =>
                        setTypeCollapsedByKey((prev) => ({
                          ...prev,
                          [sectionKey]: !typeCollapsed,
                        }))
                      }
                      className="w-full flex items-center justify-between pl-8 pr-4 py-2 text-left hover:bg-gray-100 transition-colors group/sub"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-gray-300 group-hover/sub:bg-gray-400 transition-colors" />
                        <h6 className="text-[11px] font-bold text-gray-500 uppercase tracking-wide group-hover/sub:text-gray-700">{entry.label}</h6>
                        <span className="text-[10px] text-gray-400 font-medium">{entry.groups.length}</span>
                      </div>
                      <Icon name={typeCollapsed ? "chevron-right" : "chevron-down"} size="0.75rem" className="text-gray-300" />
                    </button>
                    {!typeCollapsed && (
                      <div className="divide-y divide-gray-100 bg-white">
                        {visibleGroups.map((group) => renderGroupCard(group))}
                        <div className="p-2 flex justify-center gap-2">
                          {entry.groups.length > typeShowCount && (
                            <button
                              onClick={() =>
                                setTypeShowCountByKey((prev) => ({
                                  ...prev,
                                  [sectionKey]: typeShowCount + 5,
                                }))
                              }
                              className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide hover:underline"
                            >
                              + {entry.groups.length - typeShowCount} More Groups
                            </button>
                          )}
                          {typeShowCount > 5 && (
                            <button
                              onClick={() =>
                                setTypeShowCountByKey((prev) => ({
                                  ...prev,
                                  [sectionKey]: 5,
                                }))
                              }
                              className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide hover:underline"
                            >
                              Less
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-100">
            <Icon name="people" size="1.25rem" className="text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-tight">Groups Management</h4>
            <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Members • Defaulters • Impersonation</p>
          </div>
        </div>
        <Icon name={collapsed ? "chevron-right" : "chevron-down"} size="1rem" className="text-gray-300 transition-transform duration-200" />
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {/* Search and Quick Stats */}
          <div className="p-4 bg-gray-50/30 space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Groups</p>
                <p className="text-xs font-bold text-gray-900">{groupsCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Members</p>
                <p className="text-xs font-bold text-gray-900">{membersCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Recurring</p>
                <p className="text-xs font-bold text-gray-900">{recurringCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Non-Recur</p>
                <p className="text-xs font-bold text-gray-900">{nonRecurringCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-red-400 uppercase font-bold tracking-wider">Defaulters</p>
                <p className="text-xs font-bold text-red-600">{defaultersCountText}</p>
              </div>
            </div>

            {(summaryError || detailsError) && (
              <div className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[10px] text-red-700 font-medium">
                {summaryError || detailsError}
              </div>
            )}

            <button
              onClick={handleRefresh}
              disabled={summaryLoading || detailsLoading}
              className="w-full flex items-center justify-center gap-2 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
            >
              <Icon name="arrow-clockwise" size="0.75rem" />
              Refresh Data
            </button>
          </div>
          <div className="relative mx-1 mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="search" size="0.8rem" className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search groups, members, roles..."
              value={searchInput}
              onChange={(event) => {
                const nextValue = event.target.value
                startTransition(() => setSearchInput(nextValue))
              }}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600 bg-white"
            />
          </div>

          {/* Defaulters Section */}
          <div className="bg-white">
            <button
              onClick={() => setDefaultersCollapsed((prev) => !prev)}
              className="w-full flex items-center justify-between px-6 py-2.5 text-left hover:bg-gray-50 transition-colors group/cat"
            >
              <div className="flex items-center gap-2.5">
                <Icon name="shield-exclamation" size="0.8rem" className="text-red-500 group-hover/cat:text-red-600 transition-colors" />
                <span className="text-[11px] font-bold text-gray-700 uppercase tracking-tight group-hover/cat:text-gray-900">Defaulters</span>
                <span className="text-[10px] font-bold text-red-500">
                  {detailsLoaded ? filteredDefaulters.length : formatCount(summary?.defaultersCount)}
                </span>
              </div>
              <Icon name={defaultersCollapsed ? "chevron-right" : "chevron-down"} size="0.8rem" className="text-gray-300" />
            </button>

            {!defaultersCollapsed && (
              <div className="bg-red-50/10 divide-y divide-red-100/30">
                {!detailsLoaded ? (
                  <div className="p-4 text-center text-[11px] text-gray-400 uppercase font-bold tracking-widest">Loading...</div>
                ) : filteredDefaulters.length === 0 ? (
                  <div className="p-4 text-center text-[11px] text-gray-400 font-medium">No defaulters found.</div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {filteredDefaulters.slice(0, defaultersShowCount).map((row) => (
                      <div key={`${row.groupId}-${row.memberId}`} className="p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-bold text-gray-900 truncate">{row.memberName || "Unknown"}</p>
                          <p className="text-[10px] text-gray-500 font-medium truncate">{row.groupName}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] font-black text-red-600">{formatMoney(row.dueAmount || 0)}</span>
                          <button
                            onClick={() => onImpersonateUser?.(row.memberId, { destination: "/app/groups" })}
                            className="px-2 py-1 rounded bg-slate-900 text-white text-[9px] font-bold uppercase hover:bg-slate-800 transition-colors"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    ))}
                    <div className="p-2 flex justify-center gap-2">
                      {filteredDefaulters.length > defaultersShowCount && (
                        <button onClick={() => setDefaultersShowCount((prev) => prev + 5)} className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide hover:underline">Show More</button>
                      )}
                      {defaultersShowCount > 5 && (
                        <button onClick={() => setDefaultersShowCount(5)} className="text-[10px] font-bold text-gray-400 uppercase tracking-wide hover:underline">Less</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {renderCategorySection("recurring", recurringCollapsed, setRecurringCollapsed)}
          {renderCategorySection("non-recurring", nonRecurringCollapsed, setNonRecurringCollapsed)}
          {renderCategorySection("other", otherCollapsed, setOtherCollapsed)}
        </div>
      )}
      {groupPendingDelete && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h5 className="text-sm font-semibold text-gray-900">Delete Group</h5>
              <p className="mt-2 text-xs text-gray-600">
                You are about to permanently delete{" "}
                <span className="font-semibold text-gray-900">{groupPendingDelete?.name || "this group"}</span>.
              </p>
              <p className="mt-1 text-xs text-red-600">
                This action removes group cycles, invoices, payments, group wallet, invites, and welfare records.
              </p>
              {deleteGroupError && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-medium text-red-700">
                  {deleteGroupError}
                </div>
              )}
            </div>
            <div className="p-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteGroupModal}
                disabled={deleteGroupLoading}
                className="px-3 py-1.5 rounded border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteGroup}
                disabled={deleteGroupLoading}
                className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleteGroupLoading ? "Deleting..." : "Delete Group"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminGroupsModuleSection
